import React, { useState } from 'react';

import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers.ts';
import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { RecommendedPageBanner } from '@confluence/experiment-onboarding-crossjoin-nth-user-banner/entry-points/RecommendedPageBanner.ts';
import { RecommendedPageBannerNoContent } from '@confluence/experiment-onboarding-crossjoin-nth-user-banner/entry-points/RecommendedPageBannerNoContent.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { CROSS_JOIN_NTH_USER_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';

const styles = cssMap({
	wrapper: {
		width: '100%',
		paddingBottom: token('space.200'),
	},
});

type TrendingPagesState = {
	spaceKey?: string;
	pageIds?: string[];
};

const parseTrendingPages = (rawValue: unknown): TrendingPagesState | null => {
	if (typeof rawValue !== 'string' || !rawValue) {
		return null;
	}
	try {
		const parsed = JSON.parse(rawValue);
		if (parsed && typeof parsed === 'object') {
			return parsed as TrendingPagesState;
		}
	} catch {
		// noop — invalid JSON, treat as no banner state
	}
	return null;
};

export type RecommendedSpaceBannerProps = {
	spaceKey: string;
};

export const RecommendedSpaceBanner: React.FC<RecommendedSpaceBannerProps> = ({ spaceKey }) => {
	const [isDismissed, setIsDismissed] = useState(false);

	const { data, loading } = useGetOnboardingState(
		[CROSS_JOIN_NTH_USER_ONBOARDING.TRENDING_PAGES, CROSS_JOIN_NTH_USER_ONBOARDING.DISMISSED],
		!expValEquals('cc_activation_cross_join_nth_user_onboarding_exp', 'cohort', 'experiment'),
	);
	const { setOnboardingState } = useSetOnboardingState();

	const state = deserializeState(data);
	const trendingPages = parseTrendingPages(state?.trendingPages);
	const isBannerDismissed = state?.dismissed === true;

	const handleDismiss = () => {
		setIsDismissed(true);
		setOnboardingState({
			key: CROSS_JOIN_NTH_USER_ONBOARDING.DISMISSED,
			value: 'true',
		});
	};

	if (
		loading ||
		isDismissed ||
		isBannerDismissed ||
		!expValEquals('cc_activation_cross_join_nth_user_onboarding_exp', 'cohort', 'experiment')
	) {
		return null;
	}

	if (!trendingPages || trendingPages.spaceKey !== spaceKey) {
		return null;
	}

	const pageIds = trendingPages.pageIds ?? [];
	const hasContent = pageIds.length > 0;

	if (hasContent) {
		return (
			<Box xcss={styles.wrapper} testId="recommended-space-banner-with-content">
				<ConfluenceSmartCardProvider {...getSmartCardRenderers()}>
					<RecommendedPageBanner spaceKey={spaceKey} pageIds={pageIds} onDismiss={handleDismiss} />
				</ConfluenceSmartCardProvider>
			</Box>
		);
	}

	return (
		<Box xcss={styles.wrapper} testId="recommended-space-banner-no-content">
			<RecommendedPageBannerNoContent spaceKey={spaceKey} onDismiss={handleDismiss} />
		</Box>
	);
};
